import { FunctionComponent } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

interface InstructionPrefillOverlayProps {
	/** The suggested instruction, rendered exactly as it would be stored. */
	text: string;
	/** Click on the text — adopt it as the list's instruction and start editing. */
	onAdopt: () => void;
	/** Click on the X — discard the suggestion and leave the box empty. */
	onDismiss: () => void;
	/** Accessible name of the X button. */
	dismissLabel: string;
}

/**
 * The built-in instruction shown over an empty instruction box, as a prefill rather than as text
 * the list already holds.
 *
 * It sits on top of the textarea instead of being its value, because nothing here is persisted:
 * clicking the text hands it to the editor (from then on it is ordinary typed content, saved and
 * editable like any other), and clicking the X takes it away for good. Muted type is the only
 * signal that separates the two states, so it matches the placeholder's colour rather than the
 * text's.
 *
 * Two buttons side by side rather than one nested in the other — an X inside a clickable region
 * would swallow its own click into the adopt handler.
 */
export const InstructionPrefillOverlay: FunctionComponent<InstructionPrefillOverlayProps> = ({
	text,
	onAdopt,
	onDismiss,
	dismissLabel,
}) => (
	<>
		<button
			className="absolute inset-0 cursor-text overflow-hidden whitespace-pre-wrap bg-white px-3.5 py-3 pr-10 text-left text-sm text-text-meta"
			data-testid="instruction-prefill"
			type="button"
			onClick={onAdopt}
		>
			{text}
		</button>
		<button
			aria-label={dismissLabel}
			className="absolute right-2 top-2 cursor-pointer rounded-md p-1.5 text-text-meta opacity-0 duration-150 hover:bg-gray-100 hover:text-text focus:opacity-100 group-hover:opacity-100"
			data-testid="instruction-prefill-dismiss"
			title={dismissLabel}
			type="button"
			onClick={onDismiss}
		>
			<Icon className="h-3.5 w-3.5" icon={faXmark} />
		</button>
	</>
);
